import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, AlertTriangle, Upload, FileText } from 'lucide-react';

const DOC_TYPES = [
  'ID Proof', 'Land Record', 'Bank Statement', 'Yield Certificate',
  'Insurance Policy', 'Previous Loan Document', 'Sale Receipt', 'Purchase Invoice'
];

const STATUS_CONFIG = {
  VALID: { color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle2, label: 'Valid' },
  EXPIRED: { color: '#dc2626', bg: '#fef2f2', icon: AlertTriangle, label: 'Expired' },
  MISSING: { color: '#9ca3af', bg: '#f9fafb', icon: XCircle, label: 'Missing' },
  PENDING: { color: '#ca8a04', bg: '#fefce8', icon: AlertTriangle, label: 'Pending' },
};

const DocumentsReview = ({ assessment, journeyType }) => {
  const docStatus = assessment?.document_status || {};
  const documents = docStatus.documents || [];

  const stats = useMemo(() => ({
    total: docStatus.total_required || 0,
    valid: docStatus.valid || 0,
    expired: docStatus.expired || 0,
    missing: docStatus.missing || 0,
    uploaded: docStatus.uploaded || 0,
  }), [docStatus]);

  const isRenewal = journeyType === 'SAME_LIMIT_RENEWAL';

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="documents-review-title">
          {isRenewal ? 'Document Verification' : 'Documents'}
        </h3>
        <p className="text-sm text-gray-500">
          {isRenewal
            ? 'Only expired, missing, or changed documents need to be re-uploaded'
            : 'Upload required and recommended documents for the assessment'}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Required', value: stats.total, color: '#374151' },
          { label: 'Valid', value: stats.valid, color: '#16a34a' },
          { label: 'Expired', value: stats.expired, color: '#dc2626' },
          { label: 'Missing', value: stats.missing, color: '#9ca3af' },
        ].map(s => (
          <div key={s.label} className="text-center p-2.5 rounded-lg bg-gray-50 border border-gray-100">
            <div className="text-xl font-bold" style={{ color: s.color }}>{s.value}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Document List */}
      <div className="space-y-2">
        {documents.length > 0 ? documents.map((doc, i) => {
          const cfg = STATUS_CONFIG[doc.status] || STATUS_CONFIG.MISSING;
          const Icon = cfg.icon;

          return (
            <div
              key={i}
              className="flex items-center justify-between p-3 rounded-lg border"
              style={{ background: cfg.bg, borderColor: `${cfg.color}30` }}
              data-testid={`doc-row-${doc.document_type?.replace(/\s/g, '-')}`}
            >
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4" style={{ color: cfg.color }} />
                <div>
                  <div className="text-sm font-medium text-gray-800">{doc.document_type}</div>
                  {doc.uploaded_at && (
                    <div className="text-[10px] text-gray-500">
                      Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="text-[10px]" style={{ background: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                  <Icon className="h-3 w-3 mr-1" />
                  {cfg.label}
                </Badge>
                {(doc.status === 'EXPIRED' || doc.status === 'MISSING') && (
                  <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center cursor-pointer hover:bg-blue-200 transition-colors">
                    <Upload className="h-3.5 w-3.5 text-blue-700" />
                  </div>
                )}
              </div>
            </div>
          );
        }) : (
          DOC_TYPES.map(dtype => (
            <div key={dtype} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">{dtype}</span>
              </div>
              <Badge variant="outline" className="text-[10px] text-gray-400">Not checked</Badge>
            </div>
          ))
        )}
      </div>

      {/* Renewal note */}
      {isRenewal && (
        <div className="text-xs text-blue-700 bg-blue-50 p-3 rounded-md border border-blue-200">
          For same-limit renewal, only expired or missing documents require re-upload.
          Valid documents from the previous cycle are carried forward.
        </div>
      )}
    </div>
  );
};

export default DocumentsReview;
